import React from 'react';
import { Feature } from '@tencent/workbench';
import { CameraPresetNames, CameraPresetRotation, CameraPresetType, LightTypeNames, MaterialType, MaterialTypeNames } from '@tencent/docs-renderer-3d';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import type { IRotation3D } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
import type { IFrontAndBackFrustumUpdateInfo, IFrustumInfo, ISelectOption, IThreeDFeature } from './three-d-interface';
import type { IShapeBaseModel, IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { isWebGLEnabled } from '@src/slide/infrastructure/module/dependent/3d/webgl';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { enumerateGroupItems } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { isAttachmentModel, isGroupModel, isShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import i18n from '@i18next/i18n';
import { ColorSelectButton } from '@src/framework/workbench/drawer/components/color-select-button';
import { ITDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { INPUT_PROPS_CALLBACK_KEY } from '../../../constants';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import LabelBtn from '@framework/workbench/drawer/components/label-btn';

@Feature()
export class ThreeDFeature extends BaseDrawerFeature implements IThreeDFeature {
    public get rotation3D(): IRotation3D | undefined {
        const model = this.getSelectModel() as IShapeBaseModel;
        if (model.scene3d?.camera?.preset && model.scene3d.camera.preset !== 'orthographicFront') {
            const result = CameraPresetRotation[model.scene3d.camera.preset];
            if (result) {
                return result;
            }
        }
        return model.scene3d?.camera?.rotation;
    }

    public get depth3D(): number | undefined {
        return (this.getSelectModel() as IShapeBaseModel).shape3d?.extrusionHeight ?? 0;
    }

    public get depthColor(): ITDColor | undefined {
        return (this.getSelectModel() as IShapeBaseModel).shape3d?.extrusionColor;
    }

    public get isFlatText(): boolean | undefined {
        const model = this.getSelectModel();
        if (isGroupModel(model)) {
            let result = true;
            enumerateGroupItems(model as any, (childModel) => {
                result = result && childModel.textBodyStyle?.bodyStyle?.isFlatText;
            });
            return result;
        }
        return (this.getSelectModel() as IShapeModel).textBodyStyle?.bodyStyle?.isFlatText;
    }

    public get frontFrustumInfo(): IFrustumInfo | undefined {
        return (this.getSelectModel() as any).shape3d?.bevelTop;
    }

    public get backFrustumInfo(): IFrustumInfo | undefined {
        return (this.getSelectModel() as any).shape3d?.bevelBottom;
    }

    public get lightType(): { key: string, content: string } {
        const rigPreset = (this.getSelectModel() as IShapeBaseModel).scene3d?.lightRig?.rigPreset;
        if (rigPreset) {
            return {
                key: rigPreset,
                content: LightTypeNames[rigPreset],
            };
        }
        return {
            key: 'threePt',
            content: LightTypeNames.threePt ?? '',
        };
    }

    public get lightTypeOptions(): ISelectOption[] {
        return Object.entries(LightTypeNames).map(lightType => ({
            key: lightType[0],
            content: i18n.t(lightType[1]) as string,
        }));
    }

    public get cameraTypeOptions(): ISelectOption[] {
        return Object.entries(CameraPresetNames).map(cameraPresetType => ({
            key: cameraPresetType[0],
            content: i18n.t(cameraPresetType[1]) as string,
        }));
    }

    public get materialTypeOptions(): ISelectOption[] {
        return Object.entries(MaterialTypeNames).map(materialType => ({
            key: materialType[0],
            content: i18n.t(materialType[1]) as string,
        }));
    }

    public get lightRotationAngle(): number {
        return (this.getSelectModel() as IShapeBaseModel).scene3d?.lightRig?.rotation?.z ?? 0;
    }

    public get materialType(): MaterialType {
        return (this.getSelectModel() as IShapeBaseModel).shape3d?.materialPreset as any ?? 'warmMatte';
    }

    public get cameraPreset(): CameraPresetType | undefined {
        return (this.getSelectModel() as IShapeBaseModel).scene3d?.camera?.preset as any;
    }

    public shouldDisable3D(): boolean {
        const model = this.getSelectModel();
        if (!model || model.type === UIModelType.EMPTY) {
            return false;
        }
        if (model.type === UIModelType.MULTIPLY) {
            const multiplyModel = model as IMultiplyModel;
            return multiplyModel.children.every(model => this.shouldSingleModelDisable3D(model));
        }
        return this.shouldSingleModelDisable3D(model);
    }

    public shouldDisableComplex3DProperties(): boolean {
        let shouldDisableComplex = false;
        const model = this.getSelectModel();
        shouldDisableComplex = shouldDisableComplex || (model?.type === UIModelType.SHAPE && (model.props.style as any)?.bgShape === 'cube');
        return shouldDisableComplex || this.shouldDisable3D();
    }

    public shouldDisableShowFlatText(): boolean {
        const model = this.getSelectModel();
        return model?.type === UIModelType.IMAGE || this.shouldDisable3D();
    }

    public reset3d(): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.RESET_3D,
        });
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().reset3dProperties();
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.THREE_D)
    public handleRotation3DChange(change: IRotation3D): void {
        if (this.isInputOnlyChangeUI()) return;
        const model = this.getSelectModel() as IShapeBaseModel;
        const { rotation3D } = this;
        model.get3DProperty?.().updateCameraRotation({ ...rotation3D, ...change });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.THREE_D)
    public handleDepth3DChange(value: number): void {
        if (this.isInputOnlyChangeUI() || this.depth3D === value) return;
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateExtrusionHeight(value);
    }

    public handleDepthColorChange(color: ITDColor): void {
        const model = this.getSelectModel() as IShapeBaseModel;
        // srgb_color 格式为 “#000000ff”，3dProperty 只需要中间的6位
        model.get3DProperty?.().updateExtrusionColor(color.srgb_color.substring(1, 7));
    }

    public handleShowFlatTextChange(isFlatText: boolean): void {
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateIsFlatText(isFlatText);
    }

    public handleFrustumChange(change: IFrontAndBackFrustumUpdateInfo): void {
        const model: any = this.getSelectModel() as IShapeBaseModel;
        change.front && model.get3DProperty?.().updateBevelTop(change.front);
        change.back && model.get3DProperty?.().updateBevelBottom(change.back);
    }

    public handleLightTypeChange(lightType: string): void {
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateLightRigPreset(lightType as any);
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.THREE_D)
    public handleLightRotationAngleChange(angle: number): void {
        if (this.isInputOnlyChangeUI()) return;
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateLightRotation({ z: angle });
    }

    public handleMaterialTypeChange(materialType: MaterialType): void {
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateMaterialPreset(materialType);
    }

    public handleCameraPresetChange(cameraPreset: CameraPresetType): void {
        const model = this.getSelectModel() as IShapeBaseModel;
        model.get3DProperty?.().updateCameraPreset(cameraPreset);
    }

    public getColorSelectButton(): JSX.Element {
        return <ColorSelectButton
            onColorChange={this.handleDepthColorChange.bind(this)}
            value={this.depthColor}
            paletteButtonsStyle={{
                width: 116,
                marginLeft: 'auto',
            }}
            colorBoxStyle={{
                width: 88,
            }}
        />;
    }

    public renderReset3d(): JSX.Element {
        return <LabelBtn text={i18n.t('重置')} onClick={() => this.reset3d()}/>;
    }

    // eslint-disable-next-line complexity
    private shouldSingleModelDisable3D(model: IBaseModel): boolean {
        let shouldDisable3D = false;
        shouldDisable3D = shouldDisable3D || !isWebGLEnabled(); // webgl 不可用则禁用 3d
        shouldDisable3D = shouldDisable3D || model?.type === UIModelType.TABLE; // 表格禁用 3d
        shouldDisable3D = shouldDisable3D
            || (model?.type === UIModelType.IMAGE && (model.props as any).imageType === 'gif'); // gif 禁用 3d
        shouldDisable3D = shouldDisable3D || (model?.type === UIModelType.CONNECTOR); // 连接线禁用 3d
        // 带线条的对话框形状禁用 3d
        shouldDisable3D = shouldDisable3D
            || ((model?.props.style as any)?.bgShape?.toLowerCase().includes('bordercallout'));
        shouldDisable3D = shouldDisable3D || isAttachmentModel(model);
        shouldDisable3D = shouldDisable3D || ((model as any).isPlaceholder); // 占位符暂时屏蔽 3d
        shouldDisable3D = shouldDisable3D || (isShapeModel(model) && model.isAddon); // 第三方插件禁用 3d
        return shouldDisable3D;
    }
}
